import { Database, withLogs } from '@core';
import { SupabaseClient } from '@supabase/supabase-js';
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';

export type Coach = {
    userId: string;
    name: string;
    location: string | null;
    pgaCertified: boolean;
    elvaCertified: boolean;
};

export type UpsertCoach = Omit<Coach, 'name'>;

export const upsertCoach = withLogs(
    async (supabaseClient: SupabaseClient<Database>, coach: UpsertCoach): Promise<Coach | null> => {
        const payload = snakecaseKeys(coach);
        const { error } = await supabaseClient.from('coaches').upsert(payload, { onConflict: 'user_id' });
        if (error) throw error;
        const { data, error: fetchError } = await supabaseClient
            .rpc('get_coach', { p_user_id: coach.userId })
            .maybeSingle();
        if (fetchError) throw fetchError;
        return data ? camelcaseKeys(data) : null;
    },
    'upsertCoach',
);

export const fetchCoach = withLogs(
    async (supabaseClient: SupabaseClient<Database>, userId: string): Promise<Coach | null> => {
        const { data, error } = await supabaseClient.rpc('get_coach', { p_user_id: userId }).maybeSingle();
        if (error) throw error;
        return data ? camelcaseKeys(data) : null;
    },
    'fetchCoach',
);

export const fetchCoaches = withLogs(async (supabaseClient: SupabaseClient<Database>): Promise<Coach[]> => {
    const { data, error } = await supabaseClient.rpc('get_coaches');
    if (error) throw error;
    return data ? data.map((item) => camelcaseKeys(item)) : [];
}, 'fetchCoaches');

export const deleteCoach = withLogs(async (supabaseClient: SupabaseClient<Database>, userId: string): Promise<void> => {
    const { error } = await supabaseClient.from('coaches').delete().eq('user_id', userId);
    if (error) throw error;
}, 'deleteCoach');
